import { LicenseModule } from "@/functions/moduleConfig";
import { isSpecialAdmin } from "@/functions/environment";
import { ModifierType } from "@/functions/userModifierEnum";
import { isLISA3 } from "@/composables/useLISA3";
import { t } from "@/language/translation";
import { computed, type Ref } from "vue";

export type UserMenuEntry = {
  title: string;
  literalTitle?: string;
  /** Access-right groups displayed under this top-level sidebar module, in UI order. */
  accessRightModules?: string[];
  icon?: string;
  href?: string;
  accessRight?: string | string[];
  accessRightSome?: boolean;
  hideOnAnonym?: boolean;
  onlyOnModifier?: ModifierType;
  onlyOnModule?: LicenseModule;
  translationFunctionCode?: string;
  badge?: { text: string; class?: string };
  child?: UserMenuEntry[];
};

const menu: Ref<UserMenuEntry[]> = computed(() => [
  {
    title: "dashboard",
    icon: "fas fa-tachometer-alt",
    href: "/dashboard",
    child: [
      {
        title: "edit",
        literalTitle: t("global.edit"),
        href: "/dashboard/edit",
        hideOnAnonym: true,
        onlyOnModifier: ModifierType.LISA3,
      },
      {
        title: "achievements",
        literalTitle: t("modules.dashboard.achievements.title"),
        href: "/dashboard/achievements",
        hideOnAnonym: true,
        onlyOnModifier: ModifierType.LISA3,
      },
    ],
  },
  {
    title: "organisation",
    accessRightModules: [
      "department",
      "siteOrganization",
      "departmentOrganization",
      "shiftSchedule",
    ],
    icon: "fas fa-sitemap",
    child: [
      {
        title: "organisation",
        child: [
          { title: "SiteList", href: "/organization/site/list" },
          {
            title: "departments",
            href: "/organization/site/departments",
            accessRight: "systemSettings.admin",
          },
          {
            title: "holidays",
            href: "/holidays",
            accessRight: "systemSettings.admin",
          },
          {
            title: "costCenters",
            href: "/organization/costCenter",
            accessRight: "systemSettings.admin",
          },
          {
            title: "technicalPlaces",
            href: "/organization/technicalPlace",
            accessRight: "systemSettings.admin",
          },
        ],
      },
      {
        title: "shiftSchedule",
        child: [
          {
            title: "ShiftScheduleCCOverview",
            href: "/shiftScheduleNew/ccOverview",
            accessRight: "shiftSchedule.admin",
          },
          {
            title: "shiftScheduleNew",
            href: "/shiftScheduleNew",
            accessRight: "shiftSchedule.admin",
          },
          {
            title: "shiftScheduleTemplates",
            href: "/shifts",
            accessRight: "shiftSchedule.admin",
          },
        ],
      },
    ],
  },
  {
    title: "news",
    accessRightModules: ["news", "calendar"],
    icon: "far fa-newspaper",
    child: [
      {
        title: "newsList",
        child: [
          {
            title: "newsPlanned",
            href: "/news/n/newsTab/newsPlanned",
            accessRight: "news.admin",
          },
          {
            title: "newsActive",
            href: "/news/n/newsTab/newsActive",
            accessRight: "news.admin",
          },
          {
            title: "newsArchiv",
            href: "/news/n/newsTab/newsArchiv",
            accessRight: "news.admin",
          },
        ],
      },
      {
        title: "slider",
        child: [{ title: "sliderUpsert", href: "/news/slider", accessRight: "news.admin" }],
      },
      {
        title: "settings",
        child: [{ title: "newsSettings", href: "/news/settings", accessRight: "news.admin" }],
      },
    ],
  },
  {
    title: "problemSolving",
    accessRightModules: ["psc", "kaizen"],
    icon: "fas fa-lightbulb",
    child: [
      {
        title: "PSC",
        child: [
          { title: "overview", href: "/psc/overview/A3", accessRight: "sqdc.read" },
          {
            title: "kaizenOverview",
            href: "/psc/overview/Kaizen",
            accessRight: "sqdc.read",
          },
          {
            title: "knowledgeOverview",
            href: "/psc/overview/archive",
            accessRight: "sqdc.read",
          },
          {
            title: "eventTypesOverview",
            href: "/psc/configOverview",
            onlyOnModifier: ModifierType.LISA3,
            accessRight: "sqdc.admin",
          },
        ],
      },
    ],
  },
  {
    title: "sqdc",
    accessRightModules: ["sqdc", "datapoint", "target", "task", "flags", "accessRight"],
    icon: isLISA3.value ? "li-sqdc" : "fas fa-th",
    child: [
      {
        title: "boards",
        child: [
          { title: "kpiBoard", href: "/sqdcBoard", accessRight: "sqdc.read" },
          {
            title: "taskBoard",
            href: "/sqdcBoard/eab",
            accessRight: "sqdc.read",
            translationFunctionCode: "EABName",
          },
          {
            title: "dataMaintenanceNew",
            href: "/sqdc/dataMaintenanceNew",
            accessRight: "sqdc.admin",
          },
        ],
      },
      {
        title: "sqdcSettings",
        child: [
          { title: "boardHierarchy", href: "/boardManagement", accessRight: "sqdc.admin" },
          { title: "datapointTypes", href: "/datapointTypes", accessRight: "sqdc.admin" },
          { title: "target", href: "/target", accessRight: "sqdc.admin" },
          { title: "flags", href: "/flags", accessRight: "sqdc.admin" },
          {
            title: "mailDistribution",
            href: "/mailDistributions",
            accessRight: "sqdc.admin",
          },
          {
            title: "SQDCConfiguration",
            href: "/sqdc/configuration",
            accessRight: "systemSettings.admin",
          },
        ],
      },
      {
        title: "sqdcTemplates",
        child: [
          { title: "boardTemplates", href: "/boardTemplates", accessRight: "sqdc.admin" },
          { title: "dashlists", href: "/dashlists", accessRight: "sqdc.admin" },
          { title: "powerBI", href: "/powerBITemplates", accessRight: "sqdc.admin" },
          { title: "chartLayout", href: "/chartLayoutOverview", accessRight: "sqdc.admin" },
          {
            title: "eabShorthand",
            href: "/sqdc/EABShorthandList",
            accessRight: "sqdc.admin",
          },
          { title: "workflowOverview", href: "/workflowOverview", accessRight: "sqdc.admin" },
          {
            title: "eventTypesOverview",
            href: "/sqdc/eventActionConfig/event",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
          {
            title: "actionTypesOverview",
            href: "/sqdc/eventActionConfig/action",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
          {
            title: "SQDCWorkflowList",
            href: "/sqdc/workflow/list",
            accessRight: "sqdc.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "documents",
    accessRightModules: ["document", "folder"],
    icon: "fas fa-folder",
    href: "/file/explorer",
  },
  {
    title: "quality",
    accessRightModules: ["quality"],
    icon: "fa-solid fa-yin-yang",
    child: [
      {
        title: "charts",
        child: [
          {
            title: "chartPareto",
            href: "/chart/pareto",
            accessRight: "quality.admin",
          },
        ],
      },
      {
        title: "settings",
        child: [
          {
            title: "productFamily",
            href: "/quality/productFamilies",
            accessRight: "quality.admin",
          },
          {
            title: "errorCatalogue",
            href: "/quality/errorCatalogs",
            accessRight: "quality.admin",
          },
          { title: "setting", href: "/quality/settings", accessRight: "quality.admin" },
          {
            title: "eventTypesOverview",
            href: "/quality/configOverview",
            accessRight: "quality.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "ih",
    accessRightModules: ["ih"],
    icon: "fas fa-tools",
    child: [
      {
        title: "maintenance",
        child: [
          {
            title: "maintenanceSchedule",
            href: "/ih/maintenanceSchedule",
            accessRight: "ih.admin",
          },
          {
            title: "weakPointAnalysis",
            href: "/ih/weakPointAnalysis",
            accessRight: "ih.admin",
          },
        ],
      },
      {
        title: "settings",
        child: [
          { title: "equipments", href: "/ih/equipments", accessRight: "ih.admin" },
          { title: "IHConfigs", href: "/ih/IHConfigs", accessRight: "ih.admin" },
          {
            title: "eventTypesOverview",
            href: "/ih/configOverview",
            accessRight: "ih.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "timeStampClock",
    accessRightModules: ["timestampclock"],
    icon: "fas fa-user-clock",
    onlyOnModule: LicenseModule.TIMESTAMPCLOCK,
    child: [
      {
        title: "stamp",
        child: [
          { title: "timeStampClock", href: "/stamp/internal" },
          {
            title: "timeStampOverviewSelf",
            href: "/stamp/stampOverview/self",
            hideOnAnonym: true,
          },
          {
            title: "timeStampOverviewSelfVacation",
            href: "/stamp/vacationOverview/self",
            hideOnAnonym: true,
          },
        ],
      },
      {
        title: "admin",
        child: [
          {
            title: "timeStampDailyOverview",
            href: "/stamp/stampOverview/daily",
            accessRight: "timestampclock.admin",
          },
          {
            title: "timeStampUserOverview",
            href: "/stamp/stampOverview/userList",
            accessRight: "timestampclock.admin",
          },
          {
            title: "timeStampYearlyOverview",
            href: "/stamp/stampOverview/yearly",
            accessRight: "timestampclock.admin",
          },
          {
            title: "timeStampOverviewVacation",
            href: "/stamp/vacationOverview/admin",
            accessRight: "timestampclock.admin",
          },
          {
            title: "timeStampOverviewSubmittedSheets",
            href: "/stamp/submittedSheetsOverview",
            accessRight: "timestampclock.admin",
          },
          {
            title: "timeStampAttendanceList",
            href: "/stamp/attendanceList",
            accessRight: "timestampclock.attendance",
          },
          {
            title: "siteStampChangeRequest",
            href: "/stamp/siteStampChangeRequest",
            accessRight: "timestampclock.admin",
          },
          {
            title: "teamPlanner",
            href: "/tc/teamPlanner/site",
            accessRight: "timestampclock.admin",
          },
        ],
      },
      {
        title: "config",
        child: [
          {
            title: "userShift",
            href: "/stamp/userShiftOverview",
            accessRight: "timestampclock.admin",
          },
          {
            title: "userShiftAssociations",
            href: "/stamp/userShiftAssociations",
            accessRight: "timestampclock.admin",
          },
          {
            title: "userVacationConfig",
            href: "/stamp/userListVacationConfig",
            accessRight: "timestampclock.admin",
          },
          {
            title: "siteConfigs",
            href: "/stamp/siteConfigs",
            accessRight: "timestampclock.admin",
          },
          {
            title: "tcBlockedStampTry",
            href: "/stamp/tcBlockedStampTry",
            accessRight: "timestampclock.admin",
          },
        ],
      },
    ],
  },
  {
    title: "reporting",
    accessRightModules: ["reporting"],
    icon: "fas fa-clipboard",
    href: "/reporting",
    accessRight: "sqdc.admin",
  },
  {
    title: "audit",
    accessRightModules: ["audit"],
    icon: "fas fa-eye",
    child: [
      {
        title: "audit",
        child: [{ title: "overview", href: "/audit", accessRight: "audit.admin" }],
      },
      {
        title: "setting",
        child: [{ title: "appstore", href: "/appstore" }],
      },
    ],
  },
  {
    title: "hlp",
    accessRightModules: ["hlp", "idea"],
    icon: "fas fa-bell",
    child: [
      {
        title: "hlpSettings",
        child: [
          { title: "categoryList", href: "/idea/categoryList", accessRight: "idea.admin" },
          {
            title: "settingsList",
            href: "/idea/settingsOverview",
            accessRight: "idea.admin",
          },
          {
            title: "eventTypesOverview",
            href: "/idea/configOverview",
            accessRight: "idea.admin",
            onlyOnModifier: ModifierType.LISA3,
          },
        ],
      },
    ],
  },
  {
    title: "bde",
    accessRightModules: ["bde", "assignment", "mm"],
    icon: "fas fa-soap",
    onlyOnModule: LicenseModule.BDE,
    child: [
      {
        title: "bde",
        child: [
          { title: "BDEDashboard", href: "/bde/dashboardOverview", accessRight: "bde.write" },
          { title: "BDEUpload", href: "/bde/upload", accessRight: "bde.write" },
          {
            title: "BDEViewTpSelect",
            href: "/bde/operative/views",
            accessRight: "bde.write",
          },
        ],
      },
      {
        title: "settings",
        child: [
          { title: "mqttDeviceList", href: "/mqtt/deviceList", accessRight: "bde.admin" },
          {
            title: "BDEProductionPlanTemplateOverview",
            href: "/bde/productionPlanTemplateOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEStatusOverview",
            href: "/bde/statusOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEDataSourceOverview",
            href: "/bde/dataSourceOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEViewsOverview",
            href: "/bde/viewsOverview",
            accessRight: "bde.admin",
          },
          {
            title: "customerRequirementOverview",
            href: "/bde/customerRequirementOverview",
            accessRight: "bde.admin",
          },
          {
            title: "BDEOperatorOverview",
            href: "/bde/operatorOverview",
            accessRight: "bde.admin",
          },
          { title: "BDELabelOverview", href: "/bde/label", accessRight: "bde.admin" },
          { title: "DFDynamicForm", href: "/df/template/", accessRight: "bde.admin" },
        ],
      },
    ],
  },
  {
    title: "iot",
    accessRightModules: ["iot", "mqtt"],
    icon: "fas fa-microchip",
    onlyOnModule: LicenseModule.IOT,
    child: [
      {
        title: "management",
        child: [{ title: "iotStates", href: "/iot/states", accessRight: "iot.admin" }],
      },
      {
        title: "settings",
        child: [
          { title: "devices", href: "/iot/devices", accessRight: "iot.admin" },
          ...(isSpecialAdmin()
            ? [
                {
                  title: "iotProvisioning",
                  href: "/iot/provisioned-devices",
                  accessRight: "iot.admin",
                  onlyOnModifier: ModifierType.LISA3,
                },
              ]
            : []),
        ],
      },
    ],
  },
  {
    title: "settings",
    accessRightModules: ["user", "role", "systemSettings", "import", "instance"],
    icon: "fas fa-cogs",
    child: [
      {
        title: "userManagement",
        child: [
          { title: "user", href: "/users", accessRight: "user.admin" },
          {
            title: "roleAssignment",
            href: "/roleAssignment",
            accessRight: "user.admin",
          },
          { title: "roles", href: "/roles", accessRight: "systemSettings.admin" },
        ],
      },
      {
        title: "systemSettings",
        child: [
          {
            title: "generalSettings",
            href: "/settings/general",
            accessRight: "systemSettings.admin",
          },
          {
            title: "systemSettingsColor",
            href: "/settings/color",
            accessRight: "systemSettings.admin",
          },
        ],
      },
      {
        title: "data",
        child: [
          { title: "export", href: "/data/export", accessRight: "systemSettings.admin" },
          { title: "import", href: "/data/import", accessRight: "systemSettings.admin" },
        ],
      },
      { title: "info", child: [{ title: "changelog", href: "/changelog" }] },
      {
        title: "instance",
        child: [
          {
            title: "incomingInstance",
            href: "/instance/incomingList",
            accessRight: "systemSettings.admin",
          },
          {
            title: "externalApiStudio",
            href: "/instance/api-studio",
            accessRight: "systemSettings.admin",
          },
        ],
      },
    ],
  },
]);

export default menu;
